import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Tag, Calendar, CreditCard } from 'lucide-react';
import './Checkout.css';
import SecondaryFooter from '../Common/SecondaryFooter';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state;

  const [billingDetails, setBillingDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    gstNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [isCompanyBilling, setIsCompanyBilling] = useState(false);

  useEffect(() => {
    // Redirect if no order data
    if (!orderData || !orderData.productName || !orderData.selectedPlan) {
      navigate('/');
    }
  }, [orderData, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!billingDetails.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!billingDetails.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!billingDetails.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingDetails.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!billingDetails.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(billingDetails.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!billingDetails.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!billingDetails.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!billingDetails.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!billingDetails.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^[0-9]{6}$/.test(billingDetails.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    if (isCompanyBilling && !billingDetails.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (isCompanyBilling && billingDetails.gstNumber.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(billingDetails.gstNumber)) {
      newErrors.gstNumber = 'Invalid GST number format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Navigate to payment page with all data
      navigate('/payment', {
        state: {
          ...orderData,
          billingDetails
        }
      });
    }
  };

  if (!orderData) {
    return null;
  }

  const { productName, selectedPlan } = orderData;
  const price = selectedPlan.price || 0;
  const gst = price * 0.18;
  const total = price + gst;

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <div className="container">
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <h1>Checkout</h1>
        </div>
      </div>

      <div className="checkout-container">
        <div className="checkout-content">
          {/* Left Side - Billing Form */}
          <div className="billing-section">
            <div className="section-card">
              <h2 className="section-title">Billing Details</h2>

              <div className="billing-type-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${!isCompanyBilling ? 'active' : ''}`}
                  onClick={() => setIsCompanyBilling(false)}
                >
                  Individual
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${isCompanyBilling ? 'active' : ''}`}
                  onClick={() => setIsCompanyBilling(true)}
                >
                  Company
                </button>
              </div>

              <form onSubmit={handleSubmit} className="billing-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={billingDetails.firstName}
                      onChange={handleChange}
                      className={errors.firstName ? 'error' : ''}
                      placeholder="Enter first name"
                    />
                    {errors.firstName && <span className="error-msg">{errors.firstName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={billingDetails.lastName}
                      onChange={handleChange}
                      className={errors.lastName ? 'error' : ''}
                      placeholder="Enter last name"
                    />
                    {errors.lastName && <span className="error-msg">{errors.lastName}</span>}
                  </div>
                </div>

                {isCompanyBilling && (
                  <div className="form-group">
                    <label htmlFor="company">
                      Company Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={billingDetails.company}
                      onChange={handleChange}
                      className={errors.company ? 'error' : ''}
                      placeholder="Enter company name"
                    />
                    {errors.company && <span className="error-msg">{errors.company}</span>}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={billingDetails.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                      placeholder="your@email.com"
                    />
                    {errors.email && <span className="error-msg">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">
                      Phone Number <span className="required">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={billingDetails.phone}
                      onChange={handleChange}
                      className={errors.phone ? 'error' : ''}
                      placeholder="10-digit number"
                      maxLength="10"
                    />
                    {errors.phone && <span className="error-msg">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">
                    Address <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={billingDetails.address}
                    onChange={handleChange}
                    className={errors.address ? 'error' : ''}
                    placeholder="Street address"
                  />
                  {errors.address && <span className="error-msg">{errors.address}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={billingDetails.city}
                      onChange={handleChange}
                      className={errors.city ? 'error' : ''}
                      placeholder="City"
                    />
                    {errors.city && <span className="error-msg">{errors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">
                      State <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={billingDetails.state}
                      onChange={handleChange}
                      className={errors.state ? 'error' : ''}
                      placeholder="State"
                    />
                    {errors.state && <span className="error-msg">{errors.state}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="pincode">
                      Pincode <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="pincode"
                      name="pincode"
                      value={billingDetails.pincode}
                      onChange={handleChange}
                      className={errors.pincode ? 'error' : ''}
                      placeholder="6-digit pincode"
                      maxLength="6"
                    />
                    {errors.pincode && <span className="error-msg">{errors.pincode}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={billingDetails.country}
                      disabled
                      className="disabled-input"
                    />
                  </div>
                </div>

                {isCompanyBilling && (
                  <div className="form-group">
                    <label htmlFor="gstNumber">GST Number (Optional)</label>
                    <input
                      type="text"
                      id="gstNumber"
                      name="gstNumber"
                      value={billingDetails.gstNumber}
                      onChange={handleChange}
                      className={errors.gstNumber ? 'error' : ''}
                      placeholder="15-digit GST number"
                      maxLength="15"
                    />
                    {errors.gstNumber && <span className="error-msg">{errors.gstNumber}</span>}
                  </div>
                )}

                <button type="submit" className="proceed-btn">
                  <span>Proceed to Payment</span>
                  <CreditCard size={20} />
                </button>
              </form>
            </div>
          </div>

          {/* Right Side - Order Summary */}
          <div className="summary-section">
            <div className="section-card sticky-card">
              <h2 className="section-title">Order Summary</h2>

              <div className="product-summary">
                <div className="product-header">
                  <ShoppingCart size={20} className="icon" />
                  <h3>{productName}</h3>
                </div>

                <div className="plan-details">
                  <div className="plan-badge">
                    <Tag size={16} />
                    <span>{selectedPlan.planName}</span>
                  </div>

                  <div className="plan-duration">
                    <Calendar size={16} />
                    <span>{selectedPlan.duration === 'M' ? 'Monthly' : 'Yearly'} Subscription</span>
                  </div>
                </div>
              </div>

              <div className="price-breakdown">
                <div className="price-row">
                  <span>Subscription Price</span>
                  <span className="price">₹{price.toLocaleString('en-IN')}</span>
                </div>

                <div className="price-row">
                  <span>GST (18%)</span>
                  <span className="price">₹{gst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>

                <div className="divider"></div>

                <div className="price-row total-row">
                  <span>Total Amount</span>
                  <span className="total-price">₹{total.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="secure-notice">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1L3 3V7C3 10.5 5.5 13.5 8 14.5C10.5 13.5 13 10.5 13 7V3L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Secure checkout powered by Razorpay</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SecondaryFooter />
    </div>
  );
};

export default Checkout;